import PropTypes from 'prop-types';
import React from 'react';
import styles from './VirtualizedTable.css?module';
import {LazilyLoadFactory} from 'components/LazilyLoad';
import cx from 'classnames';

const DEFAULT_WIDTH = 50;
const MIN_WIDTH = 50;
const ROW_HEIGHT = 44;
// A smaller value here provided a faster render and made the grid feel more responsive
const OVERSCAN_COLUMN_COUNT = 1;
const OVERSCAN_ROW_COUNT = 1;
const SCROLL_BAR_SIZE = 2;

const importCellMeasurerCache = () =>
  import(/* webpackChunkName: 'react-virtualized' */ 'react-virtualized/dist/commonjs/CellMeasurer/CellMeasurerCache');

const importVirtualizedGrid = () =>
  import(/* webpackChunkName: 'react-virtualized' */ './VirtualizedGrid');

const importCellRenderer = () =>
  import(/* webpackChunkName: 'react-virtualized' */ './CellRenderer');

export class VirtualizedTable extends React.Component {
  cellMeasurerCache = new this.props.CellMeasurerCache({
    defaultWidth: DEFAULT_WIDTH,
    minWidth: MIN_WIDTH,
    fixedHeight: true,
  });

  maxWidth = window.innerWidth * 0.4;

  isHeaderRow = row => row === 0;

  isFirstColumn = column => column == 0;

  getCellAt = (row, column) =>
    this.isHeaderRow(row) ? (
      // we are wrapping the column in a div container so that we can get ellipsis to show for the header when the width is less than the column header text
      <div className={styles.headerCellContainer}>
        {this.props.getHeaderAt(column)}
      </div>
    ) : (
      // row-1 to make up for the fact that in Table.js the headers and row information are kept in separate state fields
      this.props.getCellAt(row - 1, column)
    );

  getHeaderStyleAt = column => {
    // We want to remove the percentage based style here because we are going to let
    // react-virtualized apply a pixel based width later on based on this percentage here.
    const {width, ...style} = this.props.getHeaderStyleAt(column);
    return style;
  };

  getCellStyleAt = (row, column) =>
    this.isHeaderRow(row)
      ? this.getHeaderStyleAt(column)
      : // row-1 to make up for the fact that in Table.js the headers and row information are kept in separate state fields
        this.props.getCellStyleAt(row - 1, column);

  getClassNameAt = (row, column) =>
    cx({
      [styles.headerCell]: this.isHeaderRow(row),
      [styles.tableCell]: !this.isHeaderRow(row),
      [styles.firstCellInColumn]: this.isFirstColumn(column),
    });

  getColumnWidth = props => {
    const userDefinedColumnWidths = this.props.getColumnWidthPercentages(
      props.index
    );

    if (userDefinedColumnWidths) {
      return this.props.width * userDefinedColumnWidths;
    }

    // We add +1 here because node.offsetWidth used by the VirtualizedTable CellMeasurer doesn't include subpixel width of elements
    // and so was causing ellipsis for certain cells.
    // https://www.chromestatus.com/feature/5497402177880064
    return Math.min(
      this.cellMeasurerCache.columnWidth(props) + 1,
      this.maxWidth
    );
  };

  // This overrides the default overscanIndicesGetter and provides a bubble of prerendered cells
  // all around the current visible cells so that if we scroll in any direction we have some
  // cells already rendered for us versus the default behavior which only prerenders cells outside
  // the viewport in the direction you are scrolling.
  // Available arguments to overscanIndicesGetter are as follows:
  //   direction, // One of "horizontal" or "vertical"
  //   cellCount, // Number of rows or columns in the current axis
  //   scrollDirection, // 1 (forwards) or -1 (backwards)
  //   overscanCellsCount, // Maximum number of cells to over-render in either direction
  //   startIndex, // Begin of range of visible cells
  //   stopIndex // End of range of visible
  overscanIndicesGetter = ({
    cellCount,
    overscanCellsCount,
    startIndex,
    stopIndex,
  }) => {
    return {
      overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
      overscanStopIndex: Math.min(
        cellCount - 1,
        stopIndex + overscanCellsCount
      ),
    };
  };

  render() {
    const {width, height, numberOfColumns, numberOfRows} = this.props;
    // The header is not included in number of rows but it needs to be so adding it in here
    const rowCount = numberOfRows() + 1;
    return (
      <this.props.VirtualizedGrid
        deferredMeasurementCache={this.cellMeasurerCache}
        overscanIndicesGetter={this.overscanIndicesGetter}
        overscanColumnCount={OVERSCAN_COLUMN_COUNT}
        overscanRowCount={OVERSCAN_ROW_COUNT}
        className={styles.bodyGrid}
        cellRenderer={props => (
          <this.props.CellRenderer
            {...props}
            cache={this.cellMeasurerCache}
            getCellAt={this.getCellAt}
            getClassNameAt={this.getClassNameAt}
            getStyleAt={this.getCellStyleAt}
          />
        )}
        columnCount={numberOfColumns()}
        columnWidth={this.getColumnWidth}
        height={height}
        rowCount={rowCount}
        rowHeight={ROW_HEIGHT}
        width={width}
        getScrollbarSize={() => SCROLL_BAR_SIZE}
        isScrollingOptOut={true}
      />
    );
  }
}

VirtualizedTable.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  getCellAt: PropTypes.func.isRequired,
  getCellStyleAt: PropTypes.func.isRequired,
  getHeaderAt: PropTypes.func.isRequired,
  getHeaderStyleAt: PropTypes.func.isRequired,
  style: PropTypes.object,
  numberOfColumns: PropTypes.func.isRequired,
  numberOfRows: PropTypes.func.isRequired,
  getColumnWidthPercentages: PropTypes.func.isRequired,
};

export default LazilyLoadFactory(VirtualizedTable, {
  CellMeasurerCache: importCellMeasurerCache,
  VirtualizedGrid: importVirtualizedGrid,
  CellRenderer: importCellRenderer,
});
